/** Overview tab (A7Y-4883): per-store corpus table, the quiet flag and the rules behind it. */
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@a7y/tailwind-catalyst';
import type { KeywordDatabaseOverview, KeywordDatabaseStore } from '@a7y/types';
import { formatInt, formatPct, formatUtcInstant } from '../format';

const STORE_LABEL: Record<KeywordDatabaseStore, string> = {
  appstore: 'App Store',
  playstore: 'Play Store',
};

export function OverviewTab({ overview }: { overview: KeywordDatabaseOverview }) {
  return (
    <div className="space-y-4">
      <Table dense striped data-testid="overview-stores-table">
        <TableHead>
          <TableRow>
            <TableHeader>Store</TableHeader>
            <TableHeader>Keywords</TableHeader>
            <TableHeader>Markets</TableHeader>
            <TableHeader>With volume 30 d</TableHeader>
            <TableHeader>Tracked</TableHeader>
            <TableHeader>Distinct strings</TableHeader>
            <TableHeader>Non-Latin share</TableHeader>
            <TableHeader>Last written</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {overview.stores.map((s) => (
            <TableRow key={s.store} data-testid={`store-row-${s.store}`}>
              <TableCell className="font-medium">{STORE_LABEL[s.store]}</TableCell>
              <TableCell className="tabular-nums">{formatInt(s.keywords)}</TableCell>
              <TableCell className="tabular-nums">{formatInt(s.markets)}</TableCell>
              <TableCell className="tabular-nums">{formatInt(s.withVolume30d)}</TableCell>
              <TableCell className="tabular-nums">{formatInt(s.tracked)}</TableCell>
              <TableCell className="tabular-nums">{formatInt(s.distinctStrings)}</TableCell>
              <TableCell className="tabular-nums">{formatPct(s.nonLatin, s.keywords)}</TableCell>
              <TableCell className="whitespace-nowrap">
                {formatUtcInstant(s.lastWrittenAt)}
                {s.quiet.isQuiet && (
                  <span
                    aria-label="quiet longer than expected"
                    title={`expected a write by ${formatUtcInstant(s.quiet.expectedBy)}`}
                    className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"
                  >
                    quiet
                  </span>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <div className="space-y-2 text-xs text-zinc-500 dark:text-zinc-400">
        <p data-testid="overview-distinct-total">
          A string can sit in both stores, so per-store distinct strings overlap; distinct strings
          across both stores: {formatInt(overview.distinctStringsTotal)}
        </p>
        <ul data-testid="quiet-rules" className="space-y-1">
          {overview.stores.map((s) => (
            <li key={s.store}>
              <span className="font-medium">{STORE_LABEL[s.store]} quiet rule:</span> {s.quiet.rule}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
